import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StageForm, StagePerformances } from './';
import { SectionNavbar } from '../section-navbar';
import { AssignedSponsors } from '../sponsors';
import { Loading } from '../controls';
import { useFestivalWorkspaceStore } from '@/stores/festival-workspace-store';
import {
  useStagesQuery,
  usePerformancesQuery,
  useSaveStageMutation,
  useSavePerformanceMutation,
  useRemovePerformanceMutation,
} from '@/queries/stages';
import { usePerformersQuery } from '@/queries/performers';

function StageFormContainerInner({
  stage,
  onStageSave,
  onFileUpload,
  onFileDelete,
  onPerformanceSave,
  onPerformanceDelete,
  onDateChange,
  onLocationChange,
  onLocationNameChange,
  performers,
}) {
  const formComponent = (
    <StageForm
      stage={stage}
      onStageSave={onStageSave}
      onDateChange={onDateChange}
      onLocationChange={onLocationChange}
      onLocationNameChange={onLocationNameChange}
      onFileDelete={onFileDelete}
      onFileUpload={onFileUpload}
    />
  );

  const performancesComponent = (
    <StagePerformances
      performances={stage.performances}
      handlePerformanceSave={onPerformanceSave}
      handlePerformanceDelete={onPerformanceDelete}
      stage={stage}
      performers={performers}
    />
  );

  const sponsorsComponent = (
    <AssignedSponsors objectId={stage.stageId} objectType="stage" />
  );

  const sections = [
    {
      title: 'Information',
      component: formComponent,
    },
    {
      title: 'Performances',
      component: performancesComponent,
    },
    {
      title: 'Sponsors',
      component: sponsorsComponent,
    },
  ];

  return <SectionNavbar navItems={sections} />;
}

function StageFormContainerFromRoute(props) {
  const festival = useFestivalWorkspaceStore((s) => s.festival);
  const festivalId = festival?.festivalId;
  const stageId = props.match?.params?.id;

  const stagesQ = useStagesQuery(festivalId);
  const perfQ = usePerformancesQuery(festivalId);
  const performersQ = usePerformersQuery();

  const saveStageMutation = useSaveStageMutation(festivalId);
  const savePerformanceMutation = useSavePerformanceMutation(festivalId);
  const removePerformanceMutation = useRemovePerformanceMutation(festivalId);

  const stages = stagesQ.data ?? [];
  const performances = perfQ.data ?? [];
  const performers = performersQ.data ?? [];

  const mergedStage = useMemo(() => {
    const base = stages.find((s) => String(s.stageId) === String(stageId));
    if (!base) {
      return null;
    }
    const perfs = performances.filter(
      (p) => String(p.stageId) === String(stageId),
    );
    return { ...base, performances: perfs };
  }, [stages, performances, stageId]);

  const [selectedStage, setSelectedStage] = useState(null);

  useEffect(() => {
    setSelectedStage(mergedStage);
  }, [mergedStage]);

  const onFileUpload = useCallback(
    (fileInfo) => {
      const { url, filename } = fileInfo;
      setSelectedStage((prev) => ({
        ...(prev ?? mergedStage),
        photoURL: url,
        stagePhoto: filename,
      }));
    },
    [mergedStage],
  );

  const onFileDelete = useCallback(() => {
    setSelectedStage((prev) => ({
      ...(prev ?? mergedStage),
      photoURL: null,
      stagePhoto: null,
    }));
  }, [mergedStage]);

  const onDateChange = useCallback(
    (dates) => {
      setSelectedStage((prev) => ({ ...(prev ?? mergedStage), times: dates }));
    },
    [mergedStage],
  );

  const onLocationChange = useCallback(
    (location, displayName) => {
      setSelectedStage((prev) => {
        const base = prev ?? mergedStage;
        return {
          ...base,
          location: {
            ...base.location,
            ...location,
            ...(displayName != null ? { locationName: displayName } : null),
            radius: 50,
          },
          ...(displayName != null ? { locationName: displayName } : null),
        };
      });
    },
    [mergedStage],
  );

  const onLocationNameChange = useCallback(
    (locationName) => {
      setSelectedStage((prev) => {
        const base = prev ?? mergedStage;
        return {
          ...base,
          locationName,
          location: {
            ...base.location,
            locationName,
          },
        };
      });
    },
    [mergedStage],
  );

  const onStageSave = useCallback(
    async (formStage) => {
      const base = selectedStage ?? mergedStage;
      const updatedStage = { ...base, ...formStage };
      delete updatedStage.performances;
      delete updatedStage.performers;
      try {
        await saveStageMutation.mutateAsync(updatedStage);
      } catch {
        /* mutation toast */
      }
    },
    [selectedStage, mergedStage, saveStageMutation],
  );

  const onPerformanceSave = useCallback(
    (performance) => savePerformanceMutation.mutateAsync(performance),
    [savePerformanceMutation],
  );

  const onPerformanceDelete = useCallback(
    (performance) =>
      removePerformanceMutation.mutateAsync(performance.performanceId),
    [removePerformanceMutation],
  );

  if (!festivalId || !stageId) {
    return (
      <div className="p-8 text-gray-600">
        Select a festival and open a stage from the Stages list.
      </div>
    );
  }

  if (stagesQ.isPending || perfQ.isPending || performersQ.isPending) {
    return <Loading />;
  }

  if (!mergedStage) {
    return <div className="p-8 text-gray-600">Stage not found.</div>;
  }

  const stage = selectedStage ?? mergedStage;

  return (
    <StageFormContainerInner
      stage={stage}
      onStageSave={onStageSave}
      onFileUpload={onFileUpload}
      onFileDelete={onFileDelete}
      onPerformanceSave={onPerformanceSave}
      onPerformanceDelete={onPerformanceDelete}
      onDateChange={onDateChange}
      onLocationChange={onLocationChange}
      onLocationNameChange={onLocationNameChange}
      performers={performers}
    />
  );
}

/** Modal usage from {@link Stages} passes save/delete handlers; `/stage/:id` route loads data here. */
export function StageFormContainer(props) {
  if (typeof props.onStageSave === 'function') {
    return <StageFormContainerInner {...props} />;
  }
  return <StageFormContainerFromRoute {...props} />;
}
